/**
 * MODULE: _shared/frontend-shell/src/components/nav/MobileBottomNav.tsx
 * PURPOSE: Fixed bottom tab bar for viewports below md — Dashboard, Deals, Activity, Settings.
 * DEPENDENCIES: react, react-router-dom, ./mobileBottomNavConfig
 * PUBLIC_API: MobileBottomNav
 * SIDE_EFFECTS: Fixed positioning; client navigation via React Router Link
 * AGENT_SAFETY: SAFE
 * FILE_SIZE: ~75 / 200
 * RECENT: 2026-06-08 [2.2.1340] — **`embedded`** prop for flex stack under **`ShellAssistantBottomBar`**.
 * RECENT_PRIOR: Sprint 10 [2.2.1061] — initial mobile shell bottom navigation.
 */

import { Link, useLocation } from 'react-router-dom';
import { useUnreadSignals } from '../../pages/activity/useUnreadSignals';
import {
  isMobileBottomNavTabActive,
  MOBILE_BOTTOM_NAV_TABS,
} from './mobileBottomNavConfig';

export type MobileBottomNavProps = {
  /** When true, sits in AppLayout flex column (not fixed) so Page | Assistant bar can stack above. */
  embedded?: boolean;
};

export function MobileBottomNav({ embedded = false }: MobileBottomNavProps) {
  const { pathname } = useLocation();
  const { unreadCount } = useUnreadSignals();

  return (
    <nav
      className={[
        'z-20 shrink-0 border-t border-gray-200 bg-white/95 backdrop-blur-md dark:border-gray-700 dark:bg-gray-900/95 md:hidden',
        embedded ? 'relative w-full' : 'fixed inset-x-0 bottom-0 z-40',
      ].join(' ')}
      aria-label="Mobile primary navigation"
      data-testid="mobile-bottom-nav"
      style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
    >
      <ul className="mx-auto flex h-16 max-w-lg items-stretch justify-around px-2">
        {MOBILE_BOTTOM_NAV_TABS.map((tab) => {
          const active = isMobileBottomNavTabActive(tab.id, pathname);
          const Icon = tab.icon;
          return (
            <li key={tab.id} className="flex min-w-0 flex-1">
              <Link
                to={tab.path}
                aria-current={active ? 'page' : undefined}
                className={[
                  'flex min-h-[44px] min-w-[44px] flex-1 flex-col items-center justify-center gap-0.5 rounded-lg px-1 py-1 transition-colors',
                  active
                    ? 'text-primary-600 dark:text-primary-400'
                    : 'text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200',
                ].join(' ')}
                data-testid={`mobile-bottom-nav-${tab.id}`}
              >
                <span className="relative inline-flex">
                  <Icon
                    className={`h-6 w-6 shrink-0 ${active ? 'stroke-[2.5px]' : ''}`}
                    aria-hidden
                  />
                  {tab.id === 'activity' && unreadCount > 0 ? (
                    <span
                      className="absolute -right-1 -top-0.5 h-2.5 w-2.5 rounded-full bg-red-500 ring-2 ring-white dark:ring-gray-900"
                      aria-label={`${unreadCount} unread signals`}
                      data-testid="mobile-bottom-nav-activity-unread-dot"
                    />
                  ) : null}
                </span>
                <span
                  className={`truncate text-[10px] leading-tight ${
                    active ? 'font-semibold' : 'font-medium'
                  }`}
                >
                  {tab.label}
                </span>
              </Link>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
